import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function PortSign() {
    const { token } = useParams();
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [name, setName] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [bill, setBill] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/port-sign/${token}`).then(({ data }) => { setD(data); setDone(data.status === "submitted"); })
            .catch((e) => setErr(e?.response?.data?.detail || "This link is invalid or expired"));
    }, [token]);

    const submit = async () => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("signed_name", name.trim());
            fd.append("agreed", agreed ? "true" : "false");
            fd.append("bill", bill);
            await api.post(`/port-sign/${token}`, fd);
            setDone(true);
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit — please try again"); }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-[#F8F5FC] flex items-start justify-center px-4 py-12" data-testid="port-sign-page">
            <div className="w-full max-w-xl bg-white border border-[#DDD6FE] rounded-sm p-8 space-y-5">
                <p className="font-mono text-[11px] uppercase tracking-wider text-[#D97706]">FlexLine Mobile · Number transfer</p>
                {err && <p className="text-[#D97706]" data-testid="port-sign-error">{err}</p>}
                {!d && !err && <p className="text-zinc-500">Loading…</p>}
                {d && done && (
                    <div data-testid="port-sign-done">
                        <h1 className="font-display text-3xl font-light">Signed — thank you.</h1>
                        <p className="text-zinc-600 mt-2">Your transfer has been sent to your current carrier. We'll email you when the transfer date is set. Keep your current service active until then.</p>
                    </div>
                )}
                {d && !done && (
                    <>
                        <h1 className="font-display text-3xl font-light">Authorize your number transfer</h1>
                        <div className="text-sm space-y-1 border border-[#F3E8FF] rounded-sm p-4" data-testid="port-sign-details">
                            <p><span className="text-zinc-500">Number:</span> <strong>{d.numbers.join(", ")}</strong></p>
                            <p><span className="text-zinc-500">Account holder:</span> {d.name}</p>
                            <p><span className="text-zinc-500">Service address:</span> {d.address}</p>
                            <p><span className="text-zinc-500">Account ending:</span> ••••{d.account_last4}</p>
                            {d.carrier && <p><span className="text-zinc-500">Reference:</span> {d.carrier}</p>}
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed max-h-40 overflow-y-auto" data-testid="port-sign-loa">{d.loa_text}</p>
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Latest phone bill from your current carrier (within 90 days)</p>
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setBill(e.target.files?.[0] || null)} data-testid="port-sign-bill" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Type your full legal name to e-sign</p>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="port-sign-name" />
                            {name && <p className="font-serif-italic text-2xl text-[#4C1D95]">{name}</p>}
                        </div>
                        <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="port-sign-agree" />
                            I am the account holder (or authorized), I have read the Letter of Authorization, and I agree my typed name is my electronic signature.
                        </label>
                        <Button onClick={submit} disabled={busy || !bill || !agreed || name.trim().length < 3} data-testid="port-sign-submit"
                            className="w-full bg-[#4C1D95] text-white rounded-sm h-11">{busy ? "Submitting…" : "Sign & submit transfer"}</Button>
                    </>
                )}
            </div>
        </div>
    );
}
